import loadable from '@loadable/component';
import { useState } from 'react';

import { Tab, TabList, TabPanel } from '@simplywallst/ui-core';
import { CompanyNoteModalProvider } from '@/components/Note';
import { NarrativeValuator } from '../NarrativeValuator';

import type { NarrativeForm } from '../../utils/schema';
import type { PastAnnualRevenueEarnings } from '../../types';
import type { NarrativeSidebarCompany } from '../NarrativeSidebar/narrativeSidebarFragment';
import type { CalculationBreakDownOutput } from '../Calculators/';
import { cn } from '@simplywallst/ui-core/next';

const CompanyInfo = loadable(
  () => import('@/pages/Narrative/components/CompanyInfo')
);
const Notes = loadable(() => import('@/components/MonitoringPanel'), {
  resolveComponent: (c) => c.Notes,
});

type TabPanelProps = 'valuator' | 'companyInfo' | 'notes';

type Props = {
  company: NarrativeSidebarCompany;
  onChange: (values: Partial<NarrativeForm>) => void;
  onSetFairValue: (fairValue: number) => void;
  readOnly: boolean;
  values: NarrativeForm;
  onReset: () => void;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  calculationBreakDownOutput: CalculationBreakDownOutput;
  owner: {
    displayName: string;
    publicId: string;
  };
  narrativeType: 'AI' | 'FAIR_VALUE' | 'LEGACY' | 'USER' | 'WARREN' | null;
  tabPanelsClassName?: string;
  initialFairValue: number;
};

export const NarrativeTabs = ({
  company,
  onChange,
  onSetFairValue,
  readOnly,
  values,
  onReset,
  pastAnnualRevenueEarnings,
  calculationBreakDownOutput,
  owner,
  narrativeType,
  tabPanelsClassName,
  initialFairValue,
}: Props) => {
  const [activeTab, setActiveTab] = useState<TabPanelProps>('valuator');

  return (
    <CompanyNoteModalProvider>
      <TabList
        boxShadowBottom="subtleBorder"
        templateRows="min-content"
        templateColumns={`repeat(3, max-content)`}
        paddingX="x2"
        justifyContent="flex-start"
      >
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'valuator'}
          key="valuator"
          value="valuator"
          onClick={() => setActiveTab('valuator')}
        >
          Valuator
        </Tab>
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'companyInfo'}
          key="companyInfo"
          value="companyInfo"
          onClick={() => setActiveTab('companyInfo')}
        >
          Company Info
        </Tab>
        <Tab<TabPanelProps>
          styleType="subnav"
          hasPanel
          selected={activeTab === 'notes'}
          key="notes"
          value="notes"
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </Tab>
      </TabList>

      <div
        className={cn(
          'scrollbar relative h-min overflow-y-auto',
          tabPanelsClassName
        )}
      >
        <TabPanel
          selected={activeTab === 'valuator'}
          value="valuator"
          padding="x2"
        >
          {company && (
            <NarrativeValuator
              company={company}
              values={values}
              initialFairValue={initialFairValue}
              onChange={onChange}
              onSetFairValue={onSetFairValue}
              readOnly={readOnly}
              owner={owner}
              narrativeType={narrativeType}
              onReset={onReset}
              pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
              calculationBreakDownOutput={calculationBreakDownOutput}
            />
          )}
        </TabPanel>
        <TabPanel
          selected={activeTab === 'companyInfo'}
          value="companyInfo"
          paddingLg="x2"
        >
          {company && <CompanyInfo company={company} />}
        </TabPanel>
        <TabPanel
          selected={activeTab === 'notes'}
          value="notes"
          padding="x2spacePx"
        >
          {company && (
            <Notes uniqueSymbol={company.uniqueSymbol} companyId={company.id} />
          )}
        </TabPanel>
      </div>
    </CompanyNoteModalProvider>
  );
};
